import type { StateCreator } from "../store";
import type { AppStore } from "../index";
import { Contact } from "@/data/schemas";
import { extractContactPublicKeys } from "@/data/adapters/contact_keys";
import { stateServices } from "@/state/module_api";

export interface UserSlice {
  // State
  contactsById: Record<string, Contact>;

  // Actions
  setUser: (user: Contact) => void;
  setUsers: (users: Contact[]) => void;
  updateUser: (userId: string, updates: Partial<Contact>) => void;
  removeUser: (userId: string) => void;
  clearUsers: () => void;

  // Getters (computed values - use as functions, not hooks)
  getUsersList: () => Contact[];
  getKeyMap: () => Record<string, string>;
  getUser: (userId: string) => Contact | undefined;
}

function normalizeContactInput(user: Contact): Contact {
  const normalized = {
    ...user,
    isBlocked: (user as any).isBlocked ?? (user as any).isBlock ?? false,
    isMuted: (user as any).isMuted ?? (user as any).isMute ?? false,
  };
  return normalized;
}

function syncProfilePhotoEntry(state: AppStore, user: Contact) {
  const displayPhotoMedia = (user as any).displayPhotoMedia;
  const displayPhoto = user.displayPhoto;
  if (!displayPhotoMedia?.url && !displayPhoto) {
    return;
  }

  const existing = state.profilePhotos[user.userId];
  state.profilePhotos[user.userId] = {
    userId: user.userId,
    url: displayPhotoMedia?.url || displayPhoto!,
    keyBase64: displayPhotoMedia?.keyBase64 ?? existing?.keyBase64,
    cachePath: existing?.cachePath,
    updatedAt: Date.now(),
  };
}

function resolveHandle(user: Contact): string {
  const fullHandle =
    (user.fullHandle ?? (user as any).full_handle ?? "").toString().trim();
  if (fullHandle) return fullHandle;
  if (user.username) return user.username;
  return user.userId;
}

export const createUserSlice: StateCreator<AppStore, unknown, unknown, UserSlice> = (set, get) => ({
  // Initial state
  contactsById: {},

  // Actions
  setUser: (user) =>
    set((state) => {
      const normalized = normalizeContactInput(user);
      state.contactsById[normalized.userId] = normalized;
      syncProfilePhotoEntry(state, normalized);
      stateServices.userDirectory.updateUser(normalized.userId, resolveHandle(normalized));
    }),

  setUsers: (users) =>
    set((state) => {
      users.forEach((u) => {
        const normalized = normalizeContactInput(u);
        state.contactsById[normalized.userId] = normalized;
        syncProfilePhotoEntry(state, normalized);
        stateServices.userDirectory.updateUser(normalized.userId, resolveHandle(normalized));
      });
    }),

  updateUser: (userId, updates) =>
    set((state) => {
      if (state.contactsById[userId]) {
        Object.assign(state.contactsById[userId], updates);
        const user = normalizeContactInput(state.contactsById[userId]);
        state.contactsById[userId] = user;
        syncProfilePhotoEntry(state, user);
        stateServices.userDirectory.updateUser(userId, resolveHandle(user));
      }
    }),

  removeUser: (userId) =>
    set((state) => {
      delete state.contactsById[userId];
    }),

  clearUsers: () =>
    set((state) => {
      state.contactsById = {};
    }),

  // Getters
  getUsersList: () => Object.values(get().contactsById),

  getKeyMap: () => {
    const users = Object.values(get().contactsById);
    return users
      .flatMap((u) => extractContactPublicKeys(u))
      .reduce<Record<string, string>>((map, { deviceId, publicKey }) => {
        map[deviceId] = publicKey;
        return map;
      }, {});
  },

  getUser: (userId) => get().contactsById[userId],
});
